// components/ui/InAppBrowserModal.tsx
// Reusable in-app browser modal for opening URLs within the app
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Dimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Feather } from '@expo/vector-icons';
import { useColors, ThemeColors } from '../../hooks/useColors';
import { spacing } from '../../theme';

// Conditionally import WebView only for native
let WebView: any = null;
if (Platform.OS !== 'web') {
  WebView = require('react-native-webview').WebView;
}

interface InAppBrowserModalProps {
  visible: boolean;
  url: string | null;
  title?: string;
  onClose: () => void;
}

export function InAppBrowserModal({ 
  visible, 
  url, 
  title = 'Browser',
  onClose 
}: InAppBrowserModalProps) {
  const colors = useColors();
  const styles = createStyles(colors);

  if (!url) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalWrapper}>
        <SafeAreaView style={styles.safeArea} edges={["top", "bottom"]}>
          <View style={styles.modalHeader}>
            <Text style={styles.headerText}>{title}</Text>
            <TouchableOpacity
              onPress={onClose}
              style={styles.closeButton}
            >
              <Feather name="x" size={24} color={colors.textLight} />
            </TouchableOpacity>
          </View>
          
          {Platform.OS === 'web' ? (
            // Web: Open in new window (can't use iframe due to CSP restrictions)
            <View style={styles.webContent}>
              <Text style={styles.webTitle}>{title}</Text>
              <Text style={styles.webText}>
                Click the button below to open in a new window.
              </Text>
              <View style={styles.webButtonContainer}>
                <TouchableOpacity
                  style={styles.webButton}
                  onPress={() => {
                    if (url) {
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  <Feather name="external-link" size={18} color={colors.white || '#FFFFFF'} />
                  <Text style={styles.webButtonText}>Open in New Window</Text>
                </TouchableOpacity>
                <View style={{ height: 12 }} />
                <TouchableOpacity
                  style={[styles.webButton, styles.webButtonSecondary]}
                  onPress={onClose}
                >
                  <Text style={[styles.webButtonText, styles.webButtonTextSecondary]}>Done</Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : WebView ? (
            <WebView
              source={{ uri: url }}
              style={styles.webview}
              startInLoadingState
              renderLoading={() => (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color={colors.primary} />
                </View>
              )}
            />
          ) : null}
        </SafeAreaView>
      </View>
    </Modal>
  );
}

const { height } = Dimensions.get('window');

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    modalWrapper: {
      flex: 1,
      backgroundColor: colors.background,
      ...(Platform.OS === 'web' ? {
        maxWidth: 500,
        alignSelf: 'center',
        width: '100%',
      } : {}),
    },
    safeArea: {
      flex: 1,
      backgroundColor: colors.background,
    },
    modalHeader: {
      paddingHorizontal: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      paddingTop: Platform.OS === 'web' ? spacing.md : 60,
      paddingBottom: spacing.sm,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerText: {
      fontSize: 20,
      fontWeight: '600',
      color: colors.text,
    },
    closeButton: {
      padding: spacing.xs,
    },
    webview: {
      flex: 1,
      height: height - 64,
    },
    loadingContainer: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.background,
    },
    webContent: {
      flex: 1,
      padding: spacing.xl,
      alignItems: 'center',
      justifyContent: 'center',
    },
    webTitle: {
      color: colors.text,
      fontSize: 20,
      fontWeight: '600',
      marginBottom: spacing.md,
      textAlign: 'center',
    },
    webText: {
      color: colors.textLight,
      textAlign: 'center',
      marginBottom: spacing.xl,
      paddingHorizontal: spacing.md,
      lineHeight: 22,
    },
    webButtonContainer: {
      width: '100%',
      maxWidth: 300,
    },
    webButton: {
      backgroundColor: colors.primary,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      borderRadius: spacing.sm,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      gap: spacing.sm,
    },
    webButtonSecondary: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: colors.border,
    },
    webButtonText: {
      color: colors.white || '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
    },
    webButtonTextSecondary: {
      color: colors.text,
    },
  });

